// src/data/importSessionsClient.js
//
// Client for the import-sessions + import-diffs API.
//
// S1 (LSE-ADD-DATA-MODAL-S1):
//   stageImportFile — POST /api/import-sessions
//
// S2 (LSE-ADD-DATA-MODAL-S2):
//   listImportSessions   — GET  /api/import-sessions
//   getImportSession     — GET  /api/import-sessions/:id
//   applyImportSession   — POST /api/import-sessions/:id/apply
//   rejectImportSession  — POST /api/import-sessions/:id/reject
//   decideImportDiff     — PATCH /api/import-diffs/:id
//   bulkDecideImportDiffs — POST /api/import-diffs/bulk-decide
//
// INGEST-REVIEW-SIDEBYSIDE:
//   getFieldProvenance   — GET /api/import-diffs/:diffId/provenance

import { NetworkError, errorFromResponse } from "./errors.js";
import { log } from "../utils/logger.js";
import {
  invalidatePortfolioCaches,
  emitCommandIndexStale,
} from "../services/cacheBus.js";

// ─── Helpers ──────────────────────────────────────────────────────────────────

async function apiFetch(url, opts = {}) {
  let res;
  try {
    res = await fetch(url, { credentials: "include", ...opts });
  } catch (err) {
    log.error("importSessionsClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

// ─── S1 ───────────────────────────────────────────────────────────────────────

/**
 * POST /api/import-sessions
 * Stages a file for review without writing to leases.
 *
 * @param {File}   file
 * @param {string} sourceType
 * @param {string} [locationId]
 * @returns {Promise<{ sessionId, sourceType, filename, diffCount, status, uploadedAt }>}
 */
export async function stageImportFile(file, sourceType, locationId) {
  const fd = new FormData();
  fd.append("file", file);
  fd.append("sourceType", sourceType);
  if (locationId) fd.append("locationId", locationId);

  let res;
  try {
    res = await fetch("/api/import-sessions", {
      method:      "POST",
      credentials: "include",
      body:        fd,
    });
  } catch (err) {
    log.error("importSessionsClient: network failure", { err: String(err) });
    throw new NetworkError("Failed to reach /api/import-sessions", err);
  }

  if (!res.ok) throw await errorFromResponse(res);
  return res.json();
}

// ─── S2 ───────────────────────────────────────────────────────────────────────

/**
 * GET /api/import-sessions
 * List all sessions, newest first.
 *
 * @param {object} [params]
 * @param {string} [params.status] — optional status filter
 * @returns {Promise<Array>}
 */
export async function listImportSessions(params = {}) {
  const qs = new URLSearchParams();
  if (params.status) qs.set("status", params.status);
  const url = `/api/import-sessions${qs.toString() ? `?${qs}` : ""}`;
  return apiFetch(url);
}

/**
 * GET /api/import-sessions/:id
 * Session detail + paginated diffs.
 *
 * @param {number} sessionId
 * @param {object} [params]  — { offset, limit, status, fieldName }
 * @returns {Promise<{ session, diffs, totals, pagination }>}
 */
export async function getImportSession(sessionId, params = {}) {
  const qs = new URLSearchParams();
  if (params.offset    !== undefined) qs.set("offset",    String(params.offset));
  if (params.limit     !== undefined) qs.set("limit",     String(params.limit));
  if (params.status)                  qs.set("status",    params.status);
  if (params.fieldName)               qs.set("fieldName", params.fieldName);
  const url = `/api/import-sessions/${sessionId}${qs.toString() ? `?${qs}` : ""}`;
  return apiFetch(url);
}

/**
 * POST /api/import-sessions/:id/apply
 * Apply all accepted diffs to the leases table.
 *
 * @param {number} sessionId
 * @returns {Promise<{
 *   applied, pending, status, dataType?: "leases"|"badging"|"headcount",
 *   receipt?: { added: number, updated: number, rejected: number, skipped: number },
 *   affectedLocations?: Array<{ id: string, alias: string|null }>,
 *   period?: { start: string, end: string } | null,
 * }>}
 */
export async function applyImportSession(sessionId) {
  const result = await apiFetch(`/api/import-sessions/${sessionId}/apply`, { method: "POST" });
  // Bust all portfolio caches so Lease tab, Leases board, and Plan data
  // reflect the newly applied values without requiring a page reload.
  invalidatePortfolioCaches();
  // GET /api/command-index is live but its mounted Portfolio Command
  // consumers fetch on mount, so source imports notify them after the
  // successful response has been received.
  if (result?.dataType === "headcount" || result?.dataType === "leases") {
    emitCommandIndexStale();
  }
  return result;
}

/**
 * POST /api/import-sessions/:id/reject
 * Reject all pending diffs and mark the session rejected.
 *
 * @param {number} sessionId
 * @returns {Promise<{ ok, autoRejectedDiffs }>}
 */
export async function rejectImportSession(sessionId) {
  return apiFetch(`/api/import-sessions/${sessionId}/reject`, { method: "POST" });
}

/**
 * PATCH /api/import-diffs/:id
 * Update a single diff's status.
 *
 * @param {number} diffId
 * @param {"accepted"|"rejected"|"pending"} decision
 * @returns {Promise<{ id, sessionId, leaseId, fieldName, status, decidedAt }>}
 */
export async function decideImportDiff(diffId, decision) {
  return apiFetch(`/api/import-diffs/${diffId}`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ decision }),
  });
}

/**
 * POST /api/import-diffs/bulk-decide
 * Bulk-update many diffs in one call.
 *
 * @param {object} body
 * @param {number}   body.sessionId
 * @param {string}   body.decision          — "accepted"|"rejected"|"pending"
 * @param {number[]} [body.diffIds]          — specific diff ids (takes priority)
 * @param {string}   [body.fieldName]        — all diffs with this field
 * @param {string}   [body.recommendedAction] — all diffs with this recommended_action
 * @returns {Promise<{ updated }>}
 */
export async function bulkDecideImportDiffs(body) {
  return apiFetch("/api/import-diffs/bulk-decide", {
    method:  "POST",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify(body),
  });
}

/**
 * GET /api/import-sessions/:id/audit-log
 * Returns audit_log entries linked to the given import session.
 *
 * @param {number} sessionId
 * @returns {Promise<Array<{ id, action, entityType, entityId, source, occurredAt, before, after }>>}
 */
export async function getImportSessionAuditLog(sessionId) {
  return apiFetch(`/api/import-sessions/${sessionId}/audit-log`);
}

/**
 * GET /api/import-diffs/:diffId/provenance
 * Per-field lineage trail: field_sources JSONB + audit_log + ingestion_events.
 * Returns { diffId, leaseId, fieldName, steps[] }.
 * steps[] is empty when no lineage is recorded — never fabricated.
 *
 * @param {number} diffId
 * @returns {Promise<{ diffId, leaseId, fieldName, steps: Array<{ source, actor, occurredAt, note }> }>}
 */
export async function getFieldProvenance(diffId) {
  return apiFetch(`/api/import-diffs/${diffId}/provenance`);
}

// ─── INGEST-PENDING-DISCARD ───────────────────────────────────────────────────

/**
 * POST /api/import-sessions/:id/discard
 * Discard a single staged or in_review import session.
 * Hard-deletes staged rows and diffs; keeps the session as an audit stub.
 *
 * @param {number} sessionId
 * @returns {Promise<void>} — 204 No Content
 */
export async function discardImportSession(sessionId) {
  let res;
  try {
    res = await fetch(`/api/import-sessions/${sessionId}/discard`, {
      method:      "POST",
      credentials: "include",
    });
  } catch (err) {
    log.error("importSessionsClient: network failure", { err: String(err) });
    throw new NetworkError(`Failed to reach /api/import-sessions/${sessionId}/discard`, err);
  }
  if (!res.ok) throw await errorFromResponse(res);
  // 204 has no body.
}

/**
 * POST /api/import-sessions/discard-all-pending
 * Bulk discard all staged and in_review sessions.
 *
 * @returns {Promise<{ discarded: number[] }>}
 */
export async function discardAllPendingSessions() {
  return apiFetch("/api/import-sessions/discard-all-pending", { method: "POST" });
}

/**
 * POST /api/import-sessions/:id/resume
 * Admin-only: records an audit entry marking that an admin resumed a staged
 * or in_review session, then the caller navigates into the wizard via
 * getImportSession. 403s for non-admins; 400 if the session isn't resumable.
 *
 * @param {number} sessionId
 * @returns {Promise<{ ok: boolean, sessionId: number, status: string }>}
 */
export async function resumeImportSession(sessionId) {
  return apiFetch(`/api/import-sessions/${sessionId}/resume`, { method: "POST" });
}

/**
 * GET /api/import-sessions (with ?includeSuperseded=1)
 * List all sessions including discarded/superseded — for admin audit use.
 *
 * @returns {Promise<Array>}
 */
export async function listAllImportSessions() {
  return apiFetch("/api/import-sessions?includeSuperseded=1");
}

/**
 * DELETE /api/import-sessions/purge
 * Admin-only: hard-delete all staged, in_review, discarded, and superseded
 * sessions. Applied / partially_applied sessions are never touched.
 *
 * @returns {Promise<{ deleted: number }>}
 */
export async function purgeImportSessions() {
  return apiFetch("/api/import-sessions/purge", { method: "DELETE" });
}

/**
 * PATCH /api/import-sessions/:id/confirm-date
 * Persist the admin-confirmed as-of date for a headcount session.
 * Required before the session can be applied.
 *
 * @param {number} sessionId
 * @param {string} confirmedAsOfDate  — YYYY-MM-DD
 * @returns {Promise<{ ok: boolean, confirmedAsOfDate: string }>}
 */
export async function confirmAsOfDate(sessionId, confirmedAsOfDate) {
  return apiFetch(`/api/import-sessions/${sessionId}/confirm-date`, {
    method:  "PATCH",
    headers: { "Content-Type": "application/json" },
    body:    JSON.stringify({ confirmedAsOfDate }),
  });
}
